import { ScheduleReplenishmentForm } from "@spartacus/order/root";
import { GenHandlingOption, GenContactInformation, EntryGroup, GenCartTypes, GenUnavailableItem, GenShippingGroup, GenUnavailabilityDetail } from "./cart.model";
import { GenEngineReplacementForm, GenOrderDocument, GenSubscriptionInformationItem } from "./checkout.model";
import { GenProductBadge, GenProductPurchaseLabel, GenProductScaleQuantityToPrice } from "./product.model";
import { Address, Price, Product } from "@spartacus/core";
import { GenServiceProductType } from "../enums/service-product-type";
import { GenAddressType } from "../enums/address-type.enum";
import { PaymentType } from "@spartacus/cart/base/root";
import { CheckoutStepType } from "@spartacus/checkout/base/root";
import { ICON_TYPE } from "@spartacus/storefront";
import { GenSpecialProgram } from "./special-program.model";

declare module '@spartacus/cart/base/root' {
  interface Cart {
    subscriptionInformation: GenSubscriptionInformationItem;
    handlingOptions: GenHandlingOption[];
    handlingOptionsTotal: Price;
    contactInformation: GenContactInformation;
    adjustment: Price;
    powerPlayOrder: boolean;
    prospectOrder: boolean;
    daFreeShipping?: boolean;
    tradeDiscounts: Price;
    promoDiscounts: Price;
    rootCartCode?: string;
    wellsFargoCart?: boolean;
    servicePartsDeliveryCost: Price;
    freightForwarderAddress: Address;
    isBundleCollapsed?: boolean;
    entryGroups: EntryGroup[];
    externalReferenceId: string;
    internalCalculation: boolean;
    replenishmentSchedule: ScheduleReplenishmentForm;
    isEmptyAfterBackendCleanup?: boolean;
    dropShipAddress?: Address;
    cartTypes?: GenCartTypes[];
    cartOwner: string;
    cartOwnerName: string;
    unavailableItems?: GenUnavailableItem[];
    orderInstructions?: string;
    shippingGroups: GenShippingGroup[];
    specialProgram?: string;
    sppFormIncomplete?: boolean;
    engineReplacement?: boolean;
    thirdPartyShippingCost: Price;
    orderDocuments?: GenOrderDocument[];
    orderDocumentsRequired?: boolean;
    autoShipShipmentDates?: string[];
  }

  export interface OrderEntry {
    itemListName?: string;
    navigatedToPdpFrom?: string;
    entrySource?: string;
    product?: Product;
    subscriptions?: GenSubscriptionInformationItem[];
    sppFormValue?: string;
  }

  export interface PaymentType {
    available?: boolean;
    messages: string[];
  }
}

declare module '@spartacus/order/root' {
  interface ScheduleReplenishmentForm {
    interval?: number;
    startDate?: string;
    endDate?: string;
    desiredShipmentDate?: string;
    mode?: string;
    latestDesiredShipmentDate?: string;
    earliestDesiredShipmentDate?: string;
    replenishmentEnabled?: string;
  }

  interface Order {
    subscriptionInformation: GenSubscriptionInformationItem[];
    handlingOptions: GenHandlingOption[];
    handlingOptionsTotal: Price;
    contactInformation: GenContactInformation;
    adjustment: Price;
    tradeDiscounts: Price;
    promoDiscounts: Price;
    servicePartsDeliveryCost: Price;
    replenishmentOrderCode?: string;
    internalCalculation?: boolean;
    replenishmentSchedule?: ScheduleReplenishmentForm;
    prospectOrder?: boolean;
    daFreeShipping?: boolean;
    paymentType: PaymentType;
    cartTypes?: GenCartTypes[];
    entryGroups?: EntryGroup[];
    shippingGroups?: GenShippingGroup[];
    subCartCode?: string;
    engineReplacementAttestation?: GenEngineReplacementForm;
    thirdPartyShippingCost: Price;
    orderDocuments?: GenOrderDocument[];
    orderDocumentsRequired?: boolean;
    autoShipShipmentDates?: string[];
  }
}

declare module '@spartacus/core' {
  interface Product {
    addToCartQtyMultiplier?: number;
    minOrderQuantity?: number;
    maxOrderQuantity?: number;
    autoShipAddToCartQtyMultiplier?: number;
    autoShipMinOrderQuantity?: number;
    autoShipMaxOrderQuantity?: number;
    specSheetLink?: string;
    displayModelNumber?: string;
    serviceProductType?: GenServiceProductType;
    sapMaterialNumber?: string;
    badges?: GenProductBadge[];
    displayItemAttentionMessage?: string;
    favorite?: boolean;
    erpStatus?: string;
    excludedByRegion?: boolean;
    warranties?: Product[];
    ltl?: boolean;
    flammable?: boolean;
    noAirFreight?: boolean;
    onHand?: boolean;
    unit?: string;
    urlName?: string;
    itemClass?: string;
    bundleAvailable?: boolean;
    routerLink?: string;
    scaleQuantityToPrice?: GenProductScaleQuantityToPrice[];
    purchaseRestrictedByScopeRules?: boolean;
    servicePart?: boolean;
    materialNotes?: string;
    unavailabilityDetail?: GenUnavailabilityDetail;
    purchaseLabel?: GenProductPurchaseLabel;
    engineReplacement?: boolean;
  }

  export interface Address {
    addressType?: {
      name: string;
      code: GenAddressType;
    };
    erpStatus?: string;
    saveDropShipAddress?: boolean;
    residential?: boolean;
    floorPlanAddress?: boolean;
  }

  export interface BaseSite {
    enableServiceParts?: string;
  }

  export interface B2BUnit {
    partnerId?: string;
    town?: string;
    desiredShipmentDateAvailable?: boolean;
    specialPrograms?: GenSpecialProgram[];
  }

  export interface CmsProductReferencesComponent {
    viewAllLink?: string;
  }
}

declare module '@spartacus/storefront' {
  export interface NavigationNode {
    /** A heading without a CMS entry, whose children are facet-value links. */
    isFacetGroup?: boolean;
    header?: string;
    uid?: string;
    isActive?: boolean;
    isSelected?: boolean;
    desiredShipmentDateAvailable?: boolean;
    parentNodeId?: string;
    customClickHandler?: (event?: Event) => any
  }

  export interface SearchCriteria {
    sortCode?: string;
    searchBy?: string;
    extraParams?: any;
    servicePart?: boolean;
    tab?: number;
  }

  enum ICON_TYPE {
    PIN = 'PIN',
  }
}

(ICON_TYPE as any)['PIN'] = 'PIN';

declare module '@spartacus/checkout/base/root' {
  export enum CheckoutStepType {
    SUBSCRIPTION_INFORMATION = "subscriptionInformation",
    SPECIAL_HANDLING = "specialHandling",
    FINISHED_GOODS = "finishedGoods",
    SERVICE_PARTS = "serviceParts",
    ENGINE_REPLACEMENT_FORM = "engineReplacementForm",
  }
}

(CheckoutStepType as any)['SUBSCRIPTION_INFORMATION'] = 'subscriptionInformation';
(CheckoutStepType as any)['SPECIAL_HANDLING'] = 'specialHandling';
(CheckoutStepType as any)['FINISHED_GOODS'] = 'finishedGoods';
(CheckoutStepType as any)['SERVICE_PARTS'] = 'serviceParts';
(CheckoutStepType as any)['ENGINE_REPLACEMENT_FORM'] = 'engineReplacementForm';
